if (!customElements.get('bexia-complete-look')) {
  customElements.define('bexia-complete-look', class extends HTMLElement {
    connectedCallback() {
      this.cart = JSON.parse(this.querySelector('[data-look-cart]').textContent);
      this.labels = JSON.parse(this.querySelector('[data-look-labels]').textContent);
      this.drawer = this.closest('cart-drawer');
      this.addEventListener('change', event => event.stopPropagation());
      this.observer = new MutationObserver(() => this.maybeLoad());
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      this.maybeLoad();
    }
    disconnectedCallback() { this.observer?.disconnect(); this.abort?.abort(); }
    maybeLoad() {
      if (this.loaded || !this.drawer.classList.contains('active')) return;
      this.loaded = true;
      this.abort = new AbortController();
      this.load().catch(() => { this.hidden = true; });
    }
    async get(path) {
      const response = await fetch(this.dataset.root + path, { signal: this.abort.signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      return response.json();
    }
    async load() {
      const seed = this.cart.items[this.cart.items.length - 1];
      const existing = new Set(this.cart.items.map(item => item.id));
      let products = [];
      try {
        const result = await this.get(`recommendations/products.json?product_id=${seed.id}&limit=10&intent=complementary`);
        products = result.products || [];
      } catch (error) { if (error.name === 'AbortError') throw error; }
      products = products.filter(product => !existing.has(product.id) && product.variants?.some(v => v.available));
      if (products.length < 2) {
        const text = (seed.type + ' ' + seed.title).toLowerCase();
        const categories = /short|legging|jogger|pant|skirt/.test(text) ? ['bras', 'tops'] : /bra|top|shirt|tank/.test(text) ? ['leggings', 'shorts'] : ['bras', 'tops'];
        const results = await Promise.allSettled(categories.map(category => this.get(`collections/${category}/products.json?limit=24`)));
        const colors = seed.variant.toLowerCase().split(/\s*\/\s*/);
        const candidates = results.flatMap(result => result.status === 'fulfilled' ? result.value.products || [] : []);
        candidates.sort((a, b) => this.score(b, colors) - this.score(a, colors));
        products.push(...candidates.filter(product => !existing.has(product.id) && product.variants?.some(v => v.available)));
      }
      const unique = [...new Map(products.map(product => [product.id, product])).values()].slice(0, 2);
      if (!this.isConnected || !unique.length) return;
      unique.forEach(product => this.renderProduct(product));
      this.hidden = false;
    }
    score(product, colors) {
      return colors.some(color => color.length > 2 && product.variants.some(v => v.available && v.title.toLowerCase().includes(color))) ? 1 : 0;
    }
    money(value, cents) {
      return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(cents ? Number(value) / 100 : Number(value));
    }
    renderProduct(product) {
      const cents = typeof product.variants[0].price === 'number';
      const url = this.dataset.root + 'products/' + product.handle;
      const card = document.createElement('article'); card.className = 'bexia-look__card';
      const photoLink = document.createElement('a'); photoLink.href = url; photoLink.tabIndex = -1; photoLink.setAttribute('aria-hidden', 'true');
      const image = document.createElement('img'); image.className = 'bexia-look__image'; image.alt = ''; image.loading = 'lazy'; image.width = 78; image.height = 108;
      const source = product.featured_image || product.images?.[0];
      if (source) { const imageURL = new URL(typeof source === 'string' ? source : source.src, location.origin); imageURL.searchParams.set('width', '180'); image.src = imageURL.href; }
      photoLink.append(image);
      const details = document.createElement('div');
      const title = document.createElement('a'); title.href = url; title.className = 'bexia-look__title'; title.textContent = product.title;
      const price = document.createElement('p'); price.className = 'bexia-look__price';
      const variants = product.variants.filter(v => v.available);
      price.textContent = this.money(variants[0].price, cents);
      const controls = document.createElement('div'); controls.className = 'bexia-look__controls';
      const select = document.createElement('select'); select.className = 'bexia-look__select'; select.setAttribute('aria-label', this.labels.choose + ': ' + product.title);
      select.add(new Option(this.labels.choose, ''));
      variants.forEach(variant => select.add(new Option(variant.title, String(variant.id))));
      select.addEventListener('change', () => { const variant = variants.find(v => String(v.id) === select.value); if (variant) price.textContent = this.money(variant.price, cents); });
      const button = document.createElement('button'); button.type = 'button'; button.className = 'bexia-look__add'; button.textContent = this.labels.add; button.setAttribute('aria-label', this.labels.add + ': ' + product.title);
      button.addEventListener('click', () => this.add(select, button));
      controls.append(select, button); details.append(title, price, controls); card.append(photoLink, details); this.querySelector('.bexia-look__list').append(card);
    }
    async add(select, button) {
      const status = this.querySelector('.bexia-look__status');
      if (!select.value) { status.textContent = this.labels.select; select.focus(); return; }
      button.disabled = true; button.textContent = this.labels.adding; status.textContent = '';
      try {
        const response = await fetch(routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(select.value), quantity: 1 }], sections: this.drawer.getSectionsToRender().map(section => section.id), sections_url: location.pathname }) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.labels.error);
        if (!result.sections?.['cart-drawer']) { location.assign(routes.cart_url); return; }
        this.drawer.renderContents(result);
        requestAnimationFrame(() => this.drawer.querySelector('.drawer__close')?.focus());
      } catch (error) { status.textContent = error.message || this.labels.error; }
      finally { button.disabled = false; button.textContent = this.labels.add; }
    }
  });
}
